import { Empty } from "antd";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import CardDropdown from "components/common/CardDropdown";
import SubtleBadge from "components/common/SubtleBadge";
import { useAdvanceTable } from "hooks";
import useCreditApplications from "hooks/donations/useCreditApplications";
import useFormattedDate from "hooks/useFormatDate";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React from "react";
import { Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";


// backend status enum: [INQUIRE, PAUSE, MORE_INFO, CANCEL, DENY, APPROVE, UNDER_REVIEW, ISSUE_CREDIT]

const STATUS_STYLES = {
  APPROVED: {
    bg: "#E8F7E3",
    text: "#2E7D32",
    border: "#D5EBD0",
    label: "APPROVED",
  },
  CREDIT_ISSUED: {
    bg: "#E3F2FD",
    text: "#1565C0",
    border: "#BBDEFB",
    label: "CREDIT ISSUED",
  },
  PENDING: {
    bg: "#FFF4E5",
    text: "#ED6C02",
    border: "#FFE0B2",
    label: "PENDING",
  },
  DENIED: {
    bg: "#FDECEA",
    text: "#C62828",
    border: "#F5C6C3",
    label: "DENIED",
  },
  IN_REVIEW: {
    bg: "#F3F4F6",
    text: "#374151",
    border: "#E5E7EB",
    label: "UNDER REVIEW",
  }
};

const commonStyle = {
  padding: "3px 8px",
  borderRadius: "6px",
  fontSize: "0.65rem",
  cursor: "default",
  fontWeight: 600,
};

export const CreditApplications = () => {
  const navigate = useNavigate();
  const { creditApplications, loading, error, usersLoading } =
    useCreditApplications();

  const { formatDateTime } = useFormattedDate();

  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ getValue }) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: 150 }}
          title={getValue()}
        >
          {getValue()}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "applicant",
      header: "Applicant",
      accessorFn: (row) => {
        const donor = row.applicant;
        if (!donor) return "NULL";

        // Combine first + last name, or fallback to nickname
        const fullName = `${donor.firstName || ""} ${
          donor.lastName || ""
        }`.trim();
        return fullName || donor.nickName || "NULL";
      },
      cell: ({ row }) => {
        const donor = row.original.applicant;
        if (!donor) return "NULL";
        const fullName = `${donor.firstName || ""} ${
          donor.lastName || ""
        }`.trim();
        return fullName || donor.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "createdAt",
      header: "Date applied",
      cell: ({ getValue }) => {
        return formatDateTime(getValue());
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const status = STATUS_STYLES[value] || {
          bg: "#F3F4F6",
          text: "#374151",
          border: "#E5E7EB",
          label: value || "Unknown",
        };
        return (
          <span
            className="py-2"
            style={{
              backgroundColor: status.bg,
              color: status.text,
              border: `1px solid ${status.border}`,
              ...commonStyle,
            }}
          >
            {status.label}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "documents",
      header: "Docs",
      cell: ({ getValue }) => {
        const docs = getValue();
        const label = docs.length > 0 ? "Yes" : "No";
        return (
          <span
            className="py-2"
            style={{
              backgroundColor: docs.length > 0 ? "#e8f7e3" : "#e6e8ec",
              border: docs.length > 0 ? "1px solid #d5ebd0" : "1px solid #ddd",
              color: docs.length > 0 ? "#2e7d32" : "#5d6776",
              ...commonStyle,
            }}
          >
            {label}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "references",
      header: "Refs",
      cell: ({ getValue }) => {
        const refs = getValue();
        const label = refs.length > 0 ? "Yes" : "No";
        return (
          <span
            className="py-2"
            style={{
              backgroundColor: refs.length > 0 ? "#e8f7e3" : "#e6e8ec",
              color: refs.length > 0 ? "#2e7d32" : "#5d6776",
              border: refs.length > 0 ? "1px solid #d5ebd0" : "1px solid #ddd",
              ...commonStyle,
            }}
          >
            {label}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2dd">
              <Dropdown.Item
                onClick={() =>
                  navigate(`/olitask/donations/credit-applications/${item.id}`)
                }
              >
                View
              </Dropdown.Item>
              {item.status === "APPROVED" && (
                <Guard privilege={PRIV.ISSUE_CREDIT}>
                  <Dropdown.Item
                    onClick={() =>
                      navigate(
                        `/olitask/donations/credit-application/${item.id}/issue-credit`,
                      )
                    }
                  >
                    Issue credit
                  </Dropdown.Item>
                </Guard>
              )}

              <Guard privilege={PRIV.DELETE_APPLICATIONS}>
                <Dropdown.Divider as="div" className="my-0" />
                <Dropdown.Item onClick={() => {}} className="text-danger">
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: creditApplications,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  if (loading || usersLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="hands-helping"
        title="Credit applications"
        subtitle="Manage and monitor all credit applications"
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      {creditApplications && creditApplications.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <Row className="mb-2">
            <Col md={4}>
              <AdvanceTableSearchBox />
            </Col>
          </Row>

          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle"
            rowClassName="btn-reveal-trigger text-nowrap align-middle"
            tableProps={{
              bordered: true,
              striped: true,
              className: "text-xs mb-0 overflow-hidden",
            }}
          />

          <div className="mt-3">
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </div>
        </AdvanceTableProvider>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 200 }}
        >
          <Empty description="No credit applications data available" />
        </div>
      )}
    </>
  );
};
